"use client";

import { formatShortDateWithPreferences } from "@workspace/shared-utils";
import { parseGenres } from "@workspace/types";
import {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Card, CardContent, CardHeader } from "@workspace/ui/components/card";
import { Label } from "@workspace/ui/components/label";
import { SpotifyEmbedCard } from "@/components/features/artist/cards";
import { ShowCollaboratorCard } from "@/components/features/show/cards/show-collaborator-card";
import { ShowHeadlinerCard } from "@/components/features/show/cards/show-headliner-card";
import { ShowSupportingArtistCard } from "@/components/features/show/cards/show-supporting-artist-card";
import { GenreBadgeListFromData } from "@/components/shared/genre-badge-list";
import { ClickableArtistPhoto } from "@/components/shared/photos/clickable-artist-photo";
import { SocialButtonGroup } from "@/components/shared/social-button-group";
import { SpotifyPreviewPill } from "@/components/shared/spotify-preview-pill";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import { SetVideoCard } from "@/components/features/festival/set-detail";
import { ShowReviewModal } from "@/components/features/show/modals/show-review-modal";
import { ShowSetReviewModal } from "@/components/features/show/modals/show-set-review-modal";
import { CommunityShowReviewsCard } from "@/components/features/show/review/community-show-reviews-card";
import { YourShowReviewCard } from "@/components/features/show/review/your-show-review-card";
import { ShowHeader } from "@/components/features/show/show-header";
import { ShowDetailSkeleton } from "@/components/features/show/skeletons";
import { ShowErrorState } from "@/components/features/show/states/show-error-state";
import { useShowDetails } from "@/hooks/features/show/data/migration";
import { useRealtimeShowReview } from "@/hooks/features/show/use-realtime-show-review";
import { useShowFacepile } from "@/hooks/features/show/use-show-facepile";
import { useCurrentUserDisplay } from "@/hooks/shared/use-auth";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { trackShow } from "@/lib/services/analytics";
import type { ShowArtist } from "@/lib/types/show-types";
import type { ShowFacepileData } from "@/server/api/routers/show/discovery";

interface ShowPageClientProps {
	showSlug: string;
}

interface Show {
	id: string;
	title: string;
	artist_name: string;
	artist_id?: string;
	date: string;
	time_start?: string;
	time_end?: string;
	door_open_time?: string;
	location: string;
	age_limit?: string;
	poster_url?: string;
	slug: string;
	venue_id: string;
	venue: {
		id: string;
		name: string;
		slug: string;
		address?: string;
		city: string;
		state?: string;
		country: string;
		capacity?: number;
		venue_type?: string;
	};
	show_artists?: ShowArtist[];
	tour?: string | null;
	day?: string | null;
	website_url?: string | null;
	youtube_url?: string | null;
	presents_name?: string | null;
	special_guests?: string | null;
	is_group_alias?: boolean;
	group_alias_name?: string | null;
	member_artists?: Array<{
		artist_id: string;
		display_order: number;
		artist_name: string;
		artist_slug?: string;
		artist_image_url?: string | null;
		artist_spotify_id?: string | null;
		artist_spotify_genres?: string[] | null;
		artist_manual_genres?: string[] | null;
		artist_spotify_url?: string | null;
		artist_youtube_url?: string | null;
		artist_instagram?: string | null;
		artist_twitter?: string | null;
		artist_website_url?: string | null;
	}>;
}

export const ShowDetailClient = memo(function ShowDetailClient({
	showSlug,
}: ShowPageClientProps) {
	// Fetch show details
	const { data: show, isLoading } = useShowDetails(showSlug);

	const { data: currentUser } = useCurrentUserDisplay();
	const userPreferences = useUserPreferences();

	// Review modal state
	const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
	const [selectedShowArtist, setSelectedShowArtist] = useState<ShowArtist | null>(null);
	const [isSetReviewModalOpen, setIsSetReviewModalOpen] = useState(false);

	// Get show data
	const showId =
		show && typeof show === "object" && "id" in show ? (show as Show).id : "";
	const typedShow =
		show && typeof show === "object" && "id" in show ? (show as Show) : null;
	const headlinerArtistId =
		typedShow?.artist_id ||
		typedShow?.show_artists?.find((a) => a.is_headliner)?.artist?.id;

	// Use real-time hook for consistent state between modal and display
	const { review: realtimeReview } = useRealtimeShowReview({
			showId,
			artistId: headlinerArtistId || null,
			userId: currentUser?.id || null,
			enabled: !!currentUser?.id && !!showId,
		});

	// Fetch facepile data for this show using the same pattern as show discovery cards
	const { data: facepileData } = useShowFacepile(showId ? [showId] : [], {
		enabled: !!showId,
	});

	// Transform to tRPC format for compatibility with existing components
	const userReview = useMemo(() => {
		if (!realtimeReview) return null;
		return {
			id: realtimeReview.id,
			userId: realtimeReview.userId,
			showId: realtimeReview.showId,
			artistId: realtimeReview.artistId,
			rating: realtimeReview.rating,
			content: realtimeReview.content,
			loved: realtimeReview.loved,
			sawLive: realtimeReview.sawLive,
			sawStream: realtimeReview.sawStream,
			wantToSee: realtimeReview.wantToSee,
			mustSee: realtimeReview.mustSee,
			createdAt: realtimeReview.createdAt,
			updatedAt: realtimeReview.updatedAt,
		};
	}, [realtimeReview]);

	// Transform user review for display
	const reviewForDisplay = useMemo(() => {
		if (!userReview) return null;

		// Transform from tRPC format to DbShowReview format for components
		return {
			id: userReview.id,
			user_id: userReview.userId,
			show_id: userReview.showId,
			artist_id: userReview.artistId || "",
			rating: userReview.rating || 0,
			content: userReview.content || "",
			loved: userReview.loved || false,
			saw_live: userReview.sawLive || false,
			saw_stream: userReview.sawStream || false,
			want_to_see: userReview.wantToSee || false,
			must_see: userReview.mustSee || false,
			created_at: userReview.createdAt,
			updated_at: userReview.updatedAt,
		};
	}, [userReview]);

	// Track show view when show details load
	useEffect(() => {
		if (show && typeof show === "object" && "id" in show) {
			const typedShow = show as Show;
			trackShow(typedShow.id, typedShow.title, "view");
		}
	}, [show]);

	const handleOpenReviewModal = useCallback((showArtist: ShowArtist) => {
		setSelectedShowArtist(showArtist);
		setIsSetReviewModalOpen(true);
	}, []);

	const handleCloseReviewModal = useCallback(() => {
		setIsReviewModalOpen(false);
	}, []);

	const handleCloseSetReviewModal = useCallback(() => {
		setIsSetReviewModalOpen(false);
		setSelectedShowArtist(null);
	}, []);

	const handleOpenMainReviewModal = useCallback(() => {
		setIsReviewModalOpen(true);
	}, []);

	// Track data counts
	const dataCountsRef = useRef({
		hasShow: false,
		hasReviewForDisplay: false,
		showArtistsCount: 0,
		showType: "unknown",
	});
	dataCountsRef.current = {
		hasShow: !!show,
		hasReviewForDisplay: !!reviewForDisplay,
		showArtistsCount:
			show && typeof show === "object" && "show_artists" in show
				? (show as Show).show_artists?.length || 0
				: 0,
		showType:
			show && typeof show === "object" && "id" in show ? "show" : "unknown",
	};

	// Show skeleton only when actually loading (rare with ISR)
	// This handles edge cases like expired cache, dev mode, or error recovery
	if (isLoading && !show) {
		return <ShowDetailSkeleton />;
	}

	// Show error state only when no data is available at all
	// Don't show error if we have hydrated/cached data but a background refetch failed —
	// this prevents soft 404s where Google sees "Show Not Found" on a valid HTTP 200 page
	if (!isLoading && !show) {
		return <ShowErrorState />;
	}

	if (!typedShow) {
		return <ShowErrorState />;
	}

	// Separate headliners, collaborators, and supporting artists
	const headliners =
		typedShow.show_artists?.filter(
			(artist: ShowArtist) => artist.is_headliner,
		) || [];
	const collaborators =
		typedShow.show_artists?.filter(
			(artist: ShowArtist) =>
				artist.role === "collaborator" && !artist.is_headliner,
		) || [];
	const supportingArtists =
		typedShow.show_artists?.filter(
			(artist: ShowArtist) =>
				!artist.is_headliner && artist.role === "supporting_artist",
		) || [];

	return (
		<div className="container mx-auto max-w-3xl px-4 py-4">
			<div className="flex flex-col gap-3">
				<ShowHeader show={typedShow} />

				{(headliners.length > 0 ||
					collaborators.length > 0 ||
					supportingArtists.length > 0) && (
					<div className="space-y-3">
						{headliners.length > 0 && (
							<>
								{headliners.map((headliner: ShowArtist) => (
									<ShowHeadlinerCard
										key={headliner.id}
										showArtist={headliner}
										specialGuests={typedShow.special_guests}
										showSocialLinks={{
											setlistFm: headliner.setlist_fm_url,
										}}
									/>
								))}

								{/* Spotify Embed Card - show for first headliner with spotify_id */}
								{headliners[0]?.artist?.spotify_id && (
									<SpotifyEmbedCard
										spotifyId={headliners[0].artist.spotify_id}
										artistName={headliners[0].artist.name}
									/>
								)}
							</>
						)}

						{/* YouTube Video Card */}
						{typedShow.youtube_url && <SetVideoCard youtubeUrl={typedShow.youtube_url} />}

						{/* Group Members for group alias shows */}
						{typedShow.is_group_alias && typedShow.member_artists && typedShow.member_artists.length > 0 && (
							<div className="space-y-4">
								<h2 className="font-semibold text-xl">Group Members</h2>
								<div className="flex flex-col sm:flex-row sm:flex-wrap sm:-mx-1.5 gap-3 sm:gap-0">
									{typedShow.member_artists.map((member) => {
										const artistName = member.artist_name;
										const artistImage = member.artist_image_url;
										const spotifyGenres = member.artist_spotify_genres || [];
										const manualGenres = member.artist_manual_genres || [];
										const hasGenres = spotifyGenres.length > 0 || manualGenres.length > 0;
										const hasPreview = Boolean(member.artist_spotify_id);

										if (!artistName) {
											return null;
										}

										const artistSlug = createArtistSlug(artistName, member.artist_slug);

										return (
											<div
												key={member.artist_id}
												className="w-full sm:w-1/2 sm:px-1.5 sm:pb-3"
											>
											<Card
												className="relative overflow-hidden"
											>
												<CardHeader className="p-3">
													<div className="flex gap-3">
														<div className="flex-shrink-0">
															<ClickableArtistPhoto
																href={`/artists/${artistSlug}`}
																name={artistName}
																imageUrl={artistImage}
																size="md"
																rounded="card"
																trackingData={{
																	artistId: member.artist_id,
																	artistName: artistName,
																	source: "discover",
																}}
															/>
														</div>
														<div className="min-w-0 flex-1">
															<h4 className="truncate font-semibold text-sm leading-tight">
																{artistName}
															</h4>
															{/* Social links */}
															<div className="mt-2">
																<SocialButtonGroup
																	name={artistName}
																	links={{
																		spotify: member.artist_spotify_url,
																		youtube: member.artist_youtube_url,
																		instagram: member.artist_instagram,
																		twitter: member.artist_twitter,
																		website: member.artist_website_url,
																	}}
																	size="xs"
																/>
															</div>
														</div>
													</div>
												</CardHeader>

												{(hasGenres || hasPreview) && (
													<CardContent className="px-3 pt-0 pb-3">
														<div className={hasGenres && hasPreview ? "grid grid-cols-2 gap-4" : ""}>
															{hasGenres && (
																<div className="space-y-1">
																	<Label>Genres</Label>
																	<GenreBadgeListFromData
																		spotifyGenres={spotifyGenres}
																		manualGenres={manualGenres}
																		badgeClassName="h-5 px-2 text-xs"
																	/>
																</div>
															)}

															{hasPreview && (
																<div className="space-y-1">
																	<Label>Preview</Label>
																	<SpotifyPreviewPill
																		spotifyId={member.artist_spotify_id!}
																		artistName={artistName}
																	/>
																</div>
															)}
														</div>
													</CardContent>
												)}
											</Card>
											</div>
										);
									})}
								</div>
							</div>
						)}

						{/* Your Review Section - only show for authenticated users */}
						{currentUser && (
							<div className="space-y-3">
								<h2 className="font-semibold text-xl">Your Review</h2>
								<YourShowReviewCard
									existingReview={reviewForDisplay}
									onOpenModal={handleOpenMainReviewModal}
								/>
							</div>
						)}

						{collaborators.length > 0 && (
							<div className="space-y-3">
								<h2 className="font-semibold text-xl">Collaborating Artists</h2>
								{collaborators.map((showArtist: ShowArtist) => (
									<div
										key={showArtist.id}
										onClick={() => handleOpenReviewModal(showArtist)}
										className="cursor-pointer hover:opacity-90 transition-opacity"
									>
										<ShowCollaboratorCard showArtist={showArtist} />
									</div>
								))}
							</div>
						)}

						{supportingArtists.length > 0 && (
							<div className="space-y-3">
								<h2 className="font-semibold text-xl">Supporting Artists</h2>
								{supportingArtists.map((showArtist: ShowArtist) => (
									<div
										key={showArtist.id}
										onClick={() => handleOpenReviewModal(showArtist)}
										className="cursor-pointer hover:opacity-90 transition-opacity"
									>
										<ShowSupportingArtistCard
											showArtist={showArtist}
											showSocialLinks={{
												setlistFm: showArtist.setlist_fm_url,
											}}
										/>
									</div>
								))}
							</div>
						)}
					</div>
				)}

				<div className="space-y-6">

					<div className="space-y-4">
						<h2 className="font-semibold text-xl">Community Reviews</h2>
						<CommunityShowReviewsCard showId={typedShow.id} />
					</div>
				</div>
			</div>

			{currentUser && (
				<ShowReviewModal
					isOpen={isReviewModalOpen}
					onClose={handleCloseReviewModal}
					showId={typedShow.id}
					showSlug={typedShow.slug}
					showTitle={typedShow.title}
					artistName={headliners[0]?.artist?.name || typedShow.artist_name || "Unknown Artist"}
					artistSlug={headliners[0]?.artist?.slug}
					artistId={headlinerArtistId}
					artistImageUrl={headliners[0]?.artist?.image_url || null}
					artistGenres={headliners[0]?.artist ? parseGenres(
					(headliners[0].artist.spotify_genres as string[] | undefined) || null,
					(headliners[0].artist.manual_genres as string[] | undefined) || null
				) : null}
					specialGuests={typedShow.special_guests}
					showDate={formatShortDateWithPreferences(
						typedShow.date,
						userPreferences,
					)}
					venue={typedShow.venue?.name || "Unknown Venue"}
					location={typedShow.location}
					imageUrl={typedShow.poster_url}
					socialInteractions={(facepileData as ShowFacepileData)?.[typedShow.id] || []}
				/>
			)}

			{currentUser && selectedShowArtist && (
				<ShowSetReviewModal
					isOpen={isSetReviewModalOpen}
					onClose={handleCloseSetReviewModal}
					showArtist={selectedShowArtist}
					showId={typedShow.id}
					showSlug={typedShow.slug}
					showTitle={typedShow.title}
					showDate={typedShow.date}
					venue={typedShow.venue?.name}
					location={typedShow.location}
				/>
			)}
		</div>
	);
});
